"use client";

import { useEffect, useRef, useState } from "react";
import { animate, useInView, useReducedMotion } from "framer-motion";

interface Props {
	value: number;
	duration?: number;
	suffix?: string;
	prefix?: string;
	/** Thousand separator. Set to "" to disable. */
	separator?: string;
	className?: string;
}

const format = (n: number, separator: string) => {
	if (!separator) return String(Math.round(n));
	return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

const CountUp = ({ value, duration = 2, suffix = "", prefix = "", separator = " ", className = "" }: Props) => {
	const ref = useRef<HTMLSpanElement>(null);
	const isInView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
	const shouldReduceMotion = useReducedMotion();
	const [display, setDisplay] = useState(shouldReduceMotion ? value : 0);

	useEffect(() => {
		if (!isInView) return;
		if (shouldReduceMotion) {
			setDisplay(value);
			return;
		}
		const controls = animate(0, value, {
			duration,
			ease: [0.17, 0.55, 0.55, 1],
			onUpdate: (v) => setDisplay(v),
		});
		return () => controls.stop();
	}, [isInView, value, duration, shouldReduceMotion]);

	return (
		<span ref={ref} className={className}>
			{prefix}
			{format(display, separator)}
			{suffix}
		</span>
	);
};

export default CountUp;
